import { t } from '@/i18n';
import { formatMoney } from './grid';
import type {
  Coordinate,
  EmptySquarePolicy,
  Game,
  GameStatus,
  Player,
  Result,
  Square,
} from './types';

/*
 * The host's end of the game, as data: what the three empty-square policies mean, the score
 * form, and the ledger. No Vue, no fetch — the rules live here so they are cheap to test and
 * the view is only markup.
 *
 * `SCORE_LIMITS` mirrors the score request in packages/contract/openapi.yaml.
 * openapi-typescript erases `minimum` and `maximum`, so this file and the valibot schema in
 * apps/api/src/routes/games.ts are the only two places those bounds exist. The yaml wins.
 */

export const SCORE_LIMITS = { min: 0, max: 500 } as const;

export interface PolicyChoice {
  value: EmptySquarePolicy;
  label: string;
  /**
   * What actually happens, in words. "LEAVE_BLANK" means nothing to a host — docs/UX_FLOWS.md
   * requires the consequence be spelled out, because this decides where the money goes.
   */
  consequence: string;
}

/**
 * In the order docs/UX_FLOWS.md lists them, which puts the honest default first: nobody gets
 * something for nothing.
 */
const POLICY_ORDER: readonly EmptySquarePolicy[] = ['LEAVE_BLANK', 'HOUSE', 'DISTRIBUTE_FREE'];

/**
 * A function rather than a constant, because the words come from the message catalogue and a
 * constant would be frozen in whatever language the module happened to load in.
 *
 * `forMoney` is `playsForMoney(game)` from api/grid.ts. Every consequence here is about where the
 * money goes, so in a pool played for a shot each one has a second form that says where the *win*
 * goes instead — the choice still matters, because DISTRIBUTE_FREE is what stops nobody winning.
 */
export const emptySquarePolicies = (forMoney: boolean): PolicyChoice[] =>
  POLICY_ORDER.map((value) => ({
    value,
    label: t(`lifecycle.policies.${value}.label`),
    consequence: t(`lifecycle.policies.${value}.consequence${forMoney ? '' : 'Free'}`),
  }));

/** Every field is a string, because that is what an `<input>` holds. */
export interface ScoreForm {
  teamA: string;
  teamB: string;
}

export type ScoreFieldErrors = Partial<Record<keyof ScoreForm, string>>;

/** Empty, not zero: 0-0 is a legitimate result and must be typed rather than defaulted into. */
export const blankScoreForm = (): ScoreForm => ({ teamA: '', teamB: '' });

const wholeNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
};

/** Both fields are checked every time, so a host fixes one round of mistakes rather than two. */
export function validateScore(form: ScoreForm): ScoreFieldErrors {
  const errors: ScoreFieldErrors = {};

  for (const side of ['teamA', 'teamB'] as const) {
    const raw = form[side].trim();
    if (raw === '') {
      errors[side] = t('lifecycle.score.required');
      continue;
    }

    const value = wholeNumber(raw);
    if (value === undefined) {
      errors[side] = t('lifecycle.score.whole');
      continue;
    }
    if (value > SCORE_LIMITS.max) {
      errors[side] = t('lifecycle.score.absurd');
    }
  }

  return errors;
}

/** Call only on a form that validates — the conversion assumes the fields hold numbers. */
export const scorePayload = (form: ScoreForm): { teamAScore: number; teamBScore: number } => ({
  teamAScore: Number(form.teamA.trim()),
  teamBScore: Number(form.teamB.trim()),
});

/**
 * Which square a score lands on, read off the game's own digit axes.
 *
 * `indexOf` rather than `score % 10` as the coordinate: index equals digit only in the fixed
 * mode, and the backend resolves winners this way. Used to preview the outcome *before* the host
 * commits, since scoring cannot be undone.
 */
export function winningSquareFor(
  game: Pick<Game, 'columnDigits' | 'rowDigits'>,
  score: { teamA: number; teamB: number },
): Coordinate | null {
  const col = game.columnDigits.indexOf(score.teamA % 10);
  const row = game.rowDigits.indexOf(score.teamB % 10);

  return col === -1 || row === -1 ? null : { col, row };
}

/** The payoff, in one sentence. docs/UX_FLOWS.md: the reveal deserves a moment. */
export function winnerSentence(result: Result, currency: string, forMoney: boolean): string {
  const amount = formatMoney(result.winner.amount, currency);
  // The whole branch, not the amount: "R0 to you, the host" is worse than a sentence that never
  // raises the subject. See `playsForMoney` in api/grid.ts.
  const branch = forMoney ? 'host' : 'hostFree';

  switch (result.winner.kind) {
    case 'PLAYER':
      return t(`lifecycle.winner.${branch}.player`, { name: result.winner.displayName, amount });
    case 'HOUSE':
      return t(`lifecycle.winner.${branch}.house`, { amount });
    default:
      return t(`lifecycle.winner.${branch}.nobody`, { amount });
  }
}

/** "3 squares" — a plural message, because the rule is not the same in every language. */
const squaresLabel = (count: number): string => t('common.squares', count);

/**
 * What somebody holds, in words — and the dealt squares named separately whenever there are any.
 * `squareCount` includes them and `amountOwed` does not, so a row showing only the first number
 * invites the reader to work out a debt that is wrong. Shared by both game screens.
 */
export function holdingsLabel(player: Player): string {
  const dealt = player.freeSquareCount ?? 0;
  const held = squaresLabel(player.squareCount);

  return dealt > 0 ? t('lifecycle.holdings.dealt', { held, dealt }) : held;
}

/**
 * The empty-square policy as a *player* needs it, which is not how the host chose it. The one
 * that matters is HOUSE: somebody who picked squares in a pool where the host collects on every
 * empty one is entitled to know that before the whistle, not after.
 *
 * Null before a host has locked, because there is nothing chosen to report.
 */
export function policyForPlayers(
  policy: EmptySquarePolicy | null,
  forMoney: boolean,
): string | null {
  if (!policy) return null;
  return t(`lifecycle.${forMoney ? 'playerPolicy' : 'playerPolicyFree'}.${policy}`);
}

/**
 * The result as the player reads it, which is a different sentence from the host's: it says "you"
 * when it was you, and it never says "to you, the host".
 *
 * It also never says who to pay. Some groups settle with the host up front, others pay the winner
 * at the end, and this app is the record rather than a party to either.
 *
 * The empty-square cases are the reason this is worth having. "Nobody wins" and "the host wins"
 * look identical on the grid — one ringed square nobody holds — and they are opposite outcomes.
 */
export function playerWinnerSentence(
  result: Result,
  currency: string,
  mySubjectId: string | null,
  forMoney: boolean,
): string {
  const amount = formatMoney(result.winner.amount, currency);
  const branch = forMoney ? 'player' : 'playerFree';

  switch (result.winner.kind) {
    case 'PLAYER':
      return result.winner.subjectId === mySubjectId
        ? t(`lifecycle.winner.${branch}.you`, { amount })
        : t(`lifecycle.winner.${branch}.other`, { name: result.winner.displayName, amount });
    case 'HOUSE':
      return t(`lifecycle.winner.${branch}.house`, { amount });
    default:
      return t(`lifecycle.winner.${branch}.nobody`, { amount });
  }
}

/**
 * The result read out to a room, which is a third audience again: a laptop on a coffee table
 * with eight people round it has no "you" at all. `winnerSentence` says "R30 to you, the host"
 * and `playerWinnerSentence` says "You won" — both address one reader who is not there.
 *
 * Delegating rather than a third copy of the switch: with no subject to match, every branch of
 * the player's sentence is already in the third person. The named function is what stops a
 * reader having to work that out from a null argument at the call site.
 */
export const roomWinnerSentence = (result: Result, currency: string, forMoney: boolean): string =>
  playerWinnerSentence(result, currency, null, forMoney);

export interface Ledger {
  owed: number;
  collected: number;
  outstanding: number;
  unpaidCount: number;
}

/**
 * The host's whole ledger in one pass. This app moves no money (ADR-0005), so these three
 * numbers are the entire feature.
 *
 * A player owing nothing is never counted as outstanding: somebody who was dealt free squares
 * and bought none owes nothing, and chasing them for zero would be a bug rather than a debt.
 */
export function ledger(players: Player[]): Ledger {
  let owed = 0;
  let collected = 0;
  let outstanding = 0;
  let unpaidCount = 0;

  for (const player of players) {
    owed += player.amountOwed;
    if (player.paid) {
      collected += player.amountOwed;
    } else if (player.amountOwed > 0) {
      outstanding += player.amountOwed;
      unpaidCount += 1;
    }
  }

  return { owed, collected, outstanding, unpaidCount };
}

/**
 * Whether to offer removing this player at all, and whether the squares have to go to somebody
 * rather than back on the grid. See the API's docs/GAME_RULES.md — this mirrors that rule so the
 * screen never offers a button the backend would refuse, `allowHostSuperPowers` included.
 *
 * A locked game is the interesting case: squares cannot leave the grid once it is closed, so the
 * only removal on offer is a merge — which is exactly when a host reads the ledger and notices
 * one person listed twice.
 */
export function removalWays(
  status: GameStatus,
  player: Player,
  others: Player[],
  superPowers: boolean,
): { offer: boolean; mustTransfer: boolean } {
  if (status !== 'OPEN' && status !== 'LOCKED') return { offer: false, mustTransfer: false };

  // Nothing to move, so nothing is at stake and no destination is needed — which is also why
  // `allowHostSuperPowers` does not reach this branch: the flag protects squares, and there are
  // none. A stray empty row on the ledger still wants tidying up.
  if (player.squareCount === 0) return { offer: true, mustTransfer: false };

  // Past here every form moves somebody else's paid-for squares, whether onto the grid or into
  // another player's hands, so both are gated together.
  if (!superPowers) return { offer: false, mustTransfer: false };

  if (status === 'OPEN') return { offer: true, mustTransfer: false };

  return { offer: others.length > 0, mustTransfer: true };
}

/**
 * What the removal will actually do, in one sentence, shown before the host confirms. The
 * difference between the two forms is where somebody's paid-for squares end up, which is not
 * something to leave a host to infer from a button.
 *
 * "they" throughout: this app knows a display name and nothing else about anybody.
 */
export function removalSentence(player: Player, destination: Player | null): string {
  const name = player.displayName;

  if (player.squareCount === 0) return t('lifecycle.removal.nothingHeld', { name });

  const held = squaresLabel(player.squareCount);

  return destination
    ? t('lifecycle.removal.transfer', { name, held, destination: destination.displayName })
    : t('lifecycle.removal.backOnGrid', { name, held });
}

/**
 * The third destination: nobody. Used as the `<select>` value and as the destination itself, so
 * there is one value with one meaning rather than a mapping between two.
 *
 * It cannot collide with a real destination, because every subject id carries a `guest_` or
 * `user_` prefix — see `identity.ts` in the API. `''` stays "nothing chosen yet".
 */
export const BACK_ON_THE_GRID = 'GRID';

export interface Handover {
  /**
   * The squares the host tapped, each with whoever holds it — null when it is empty. The two cases
   * differ by that, per square, which is the whole feature: one is a mis-tap being fixed, the other
   * is a square being bought in somebody's name.
   *
   * A square rather than a ledger row, because a tap on the grid is where this comes from — and
   * because `free` is what says whether moving it costs the recipient anything.
   */
  squares: {
    /** As `squareKey` writes it: `9-0`. */
    key: string;
    holder: Pick<Square, 'subjectId' | 'displayName' | 'free'> | null;
  }[];
  /**
   * Who they are going to: a player, `BACK_ON_THE_GRID` for nobody, or null until the host has
   * chosen. All three start with the same tap on the grid, which is why they are one control.
   */
  destination: Player | typeof BACK_ON_THE_GRID | null;
  /** Only the squares nobody has bought spend this, which is why they say an amount out loud. */
  pricePerSquare: number;
  currency: string;
}

export interface HandoverPlan {
  /** What this will do, and to whose money, before the host commits. */
  sentence: string;
  /** What the confirm button promises. Names or counts the squares once a player is chosen. */
  confirmLabel: string;
  /** Null when the host may go ahead; the reason in words when the selection cannot be given. */
  refusal: string | null;
}

/**
 * Whether to offer the override at all, and who may receive a square. Mirrors the API's rule in
 * docs/GAME_RULES.md so the screen never offers a button the backend would refuse: `OPEN` and
 * `LOCKED` only, refused once a score exists, and off entirely without `allowHostSuperPowers`.
 *
 * Unlike the single-square version this cannot exclude the holder, because a selection can hold
 * squares belonging to several people — `handoverPlan` names the clash instead, which is also the
 * only form that can tell the host what to do about it.
 */
export function handoverWays(
  status: GameStatus,
  players: Player[],
  superPowers: boolean,
): { offer: boolean; destinations: Player[]; canPutBack: boolean } {
  // Every destination goes together when the powers are off, the empty-square case included —
  // that is the case the flag is most obviously for, because handing somebody a square nobody has
  // bought makes them owe money on the strength of a message the app never saw.
  const offer = superPowers && (status === 'OPEN' || status === 'LOCKED') && players.length > 0;

  /*
   * Nobody is a destination only while the game is open. A square changing hands moves no money
   * off the grid and so survives a lock; one coming off it takes money out of the pot, and a
   * locked pot is what everybody is playing for. The same line a plain player removal draws.
   */
  return { offer, destinations: players, canPutBack: offer && status === 'OPEN' };
}

/** `Three` up to five, then the numeral — a sentence should not spell out "twenty-three". */
const spelled = (count: number): string =>
  count <= 5 ? t(`lifecycle.handover.counts.${count}`) : String(count);

/** `9-0 and 9-1`, or `9-0, 9-1 and 9-2` — a list in a sentence, not a comma-separated field. */
const listed = (keys: string[]): string =>
  keys.length <= 1
    ? (keys[0] ?? '')
    : `${keys.slice(0, -1).join(', ')} ${t('common.listAnd')} ${keys[keys.length - 1] as string}`;

/**
 * What handing these squares over will do, in words, before the host commits.
 *
 * The squares nobody has bought are the ones that matter: they make somebody owe money on the
 * strength of a message the app never saw, so the sentence says the amount and names whose debt it
 * becomes. A move says the opposite just as plainly, because a host who cannot tell the two apart
 * will use the wrong one — and a mixed handful says both halves, because a single count would lie
 * about half of it.
 *
 * One square is named; several are counted. "Puts Nadia on 1 square" is worse than "Puts Nadia on
 * 9-0", and reciting ten coordinates is worse than counting them.
 */
export function handoverPlan(
  handover: Handover,
  limit: { maxSquaresPerPlayer: number },
): HandoverPlan {
  const { squares, destination, pricePerSquare, currency } = handover;

  /*
   * Read off the price rather than taken as a flag, so the two cannot disagree. In a pool played
   * for a shot, every clause below about a debt, a pot or a payment is not merely zero — it is
   * about something that does not exist, so each of those sentences has a second form. What does
   * *not* change is which squares move and where they end up, which is the whole point of the
   * panel. See `playsForMoney` in api/grid.ts.
   */
  const suffix = pricePerSquare > 0 ? '' : 'Free';

  const held = squares.filter((square) => square.holder);
  const empty = squares.filter((square) => !square.holder);
  // What the recipient will actually owe more for: everything nobody was dealt. A dealt square
  // was never bought, so moving one changes nothing about what anybody owes.
  const payable = squares.filter((square) => !square.holder?.free).length;
  const money = formatMoney(payable * pricePerSquare, currency);
  const one = squares.length === 1 ? squares[0] : null;
  const count = squaresLabel(squares.length);

  /*
   * Nobody. A separate branch rather than more ternaries in the two below: the arithmetic runs the
   * other way — the pot *drops*, and what the holders owe drops with it — and every clause about a
   * recipient's debt or their limit is meaningless here.
   */
  if (destination === BACK_ON_THE_GRID) {
    const holders = new Set(held.map((square) => square.holder?.subjectId));
    const owner = holders.size === 1 ? held[0]?.holder?.displayName : null;
    const what = one ? one.key : count;
    // Only what is actually coming off the grid, so the figure stays honest beside a refusal:
    // `money` above counts the empty squares too, because for the other destinations they are
    // exactly what the recipient starts paying for.
    const fromThePot = formatMoney(
      held.filter((square) => !square.holder?.free).length * pricePerSquare,
      currency,
    );

    return {
      /*
       * "Nobody holds 9-0" is the mirror of the already-holds clash below, and needs the same
       * answer: which tap to undo. A square nobody holds has nothing to put back, and the API
       * refuses the whole batch for it rather than quietly skipping it.
       */
      refusal:
        empty.length > 0
          ? t(
              'lifecycle.handover.putBack.refusal',
              { keys: listed(empty.map((square) => square.key)) },
              empty.length,
            )
          : null,
      /*
       * One person is named and their debt is named with it, because that is the conversation the
       * host is having — "take 5-1 off me". Several people are counted: reciting three names is
       * worse than a number, and the pot is what they all care about anyway.
       *
       * The whole sentence is pluralised rather than the pronoun inside it: "it" and "them" are
       * not two words that can be swapped in every language.
       */
      sentence:
        held.length === 0
          ? // Every one of them empty. Refused, but the sentence is on screen beside the refusal
            // and "off 0 players" would read like a bug rather than a mis-tap.
            t('lifecycle.handover.putBack.nothingToPutBack', {
              keys: listed(squares.map((square) => square.key)),
            })
          : owner
            ? t(
                `lifecycle.handover.putBack.fromOwner${suffix}`,
                { what, owner, amount: fromThePot },
                squares.length,
              )
            : t(
                `lifecycle.handover.putBack.fromSeveral${suffix}`,
                { what, holders: holders.size, amount: fromThePot },
                squares.length,
              ),
      confirmLabel: t('lifecycle.handover.putBack.confirm', { what }),
    };
  }

  if (!destination) {
    return {
      sentence: one
        ? one.holder
          ? t(`lifecycle.handover.choose.oneHeld${suffix}`, {
              holder: one.holder.displayName,
              key: one.key,
            })
          : t(`lifecycle.handover.choose.oneEmpty${suffix}`, { key: one.key, amount: money })
        : held.length === 0
          ? t(`lifecycle.handover.choose.manyEmpty${suffix}`, {
              count: spelled(squares.length),
              amount: money,
            })
          : t('lifecycle.handover.choose.manyMixed', {
              count: spelled(squares.length),
              held: held.length,
            }),
      // Says what it wants rather than pretending to be ready. The button is disabled either way.
      confirmLabel: one
        ? one.holder
          ? t('lifecycle.handover.choose.moveIt')
          : t('lifecycle.handover.choose.handOutIt')
        : held.length === squares.length
          ? t('lifecycle.handover.choose.moveThem')
          : t('lifecycle.handover.choose.handOutThem'),
      refusal: null,
    };
  }

  /*
   * Dealt squares are left out of the count for the same reason the API leaves them out of its
   * limit: nobody bought them, and a DISTRIBUTE_FREE deal puts every player far past the limit, so
   * counting them would refuse every override in a dealt game.
   */
  const paid = destination.squareCount - (destination.freeSquareCount ?? 0);
  const theirs = squares.filter((square) => square.holder?.subjectId === destination.subjectId);

  const refusal =
    theirs.length > 0
      ? theirs.length === squares.length && one
        ? t('lifecycle.handover.refusal.alreadyHoldsOne', {
            name: destination.displayName,
            key: one.key,
          })
        : t(
            'lifecycle.handover.refusal.alreadyHoldsSome',
            {
              name: destination.displayName,
              keys: listed(theirs.map((square) => square.key)),
            },
            theirs.length,
          )
      : paid + payable > limit.maxSquaresPerPlayer
        ? t(
            `lifecycle.handover.refusal.overLimit${suffix}`,
            {
              name: destination.displayName,
              total: paid + payable,
              max: limit.maxSquaresPerPlayer,
            },
            paid + payable,
          )
        : null;

  /*
   * Two different numbers, and a host needs both. A square that changes hands was already bought,
   * so the *pot* does not move — but the recipient owes for it now and whoever held it does not.
   * Saying only "no money moves" would be true of the pot and false of the ledger.
   */
  const intoThePot = formatMoney(
    empty.filter((square) => !square.holder?.free).length * pricePerSquare,
    currency,
  );

  const name = destination.displayName;

  const sentence = one
    ? one.holder
      ? t(`lifecycle.handover.plan.moveOne${suffix}`, {
          key: one.key,
          holder: one.holder.displayName,
          name,
          amount: money,
        })
      : t(`lifecycle.handover.plan.putOnOne${suffix}`, { name, key: one.key, amount: money })
    : held.length === 0
      ? t(`lifecycle.handover.plan.putOnMany${suffix}`, { name, count, amount: money })
      : empty.length === 0
        ? t(`lifecycle.handover.plan.moveMany${suffix}`, { name, count, amount: money })
        : t(`lifecycle.handover.plan.mixed${suffix}`, {
            name,
            count,
            held: held.length,
            empty: empty.length,
            intoThePot,
            amount: money,
          });

  const confirmLabel = one
    ? one.holder
      ? t('lifecycle.handover.confirm.moveOne', { key: one.key, name })
      : t('lifecycle.handover.confirm.putOnOne', { name, key: one.key })
    : held.length === 0
      ? t('lifecycle.handover.confirm.putOnMany', { name, count })
      : empty.length === 0
        ? t('lifecycle.handover.confirm.moveMany', { count, name })
        : t('lifecycle.handover.confirm.giveMany', { name, count });

  return { sentence, confirmLabel, refusal };
}
